import { Controller } from "@hotwired/stimulus"

// Lets people fill a date/time by typing digits into small linked boxes
// (day/month/year/hour/minute) that auto-advance to the next box as each
// segment fills up — no calendar dropdown ever appears. Replaces the
// browser's native datetime-local widget on the game form, which testers
// found fiddly (had to hunt for the right day on a tiny calendar popup
// just to schedule a match). The combined value is written into a hidden
// field in "YYYY-MM-DDTHH:MM" format, which Rails parses the same way it
// would have parsed a native datetime-local input.
export default class extends Controller {
  static targets = ["day", "month", "year", "hour", "minute", "hidden"]

  connect() {
    this.hydrate()
  }

  // Pre-fills the boxes from the hidden field's initial value, so editing
  // an existing game shows its current date/time instead of blank boxes.
  hydrate() {
    const match = this.hiddenTarget.value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
    if (!match) return

    const [, year, month, day, hour, minute] = match
    this.dayTarget.value = day
    this.monthTarget.value = month
    this.yearTarget.value = year
    this.hourTarget.value = hour
    this.minuteTarget.value = minute
  }

  segments() {
    return [this.dayTarget, this.monthTarget, this.yearTarget, this.hourTarget, this.minuteTarget]
  }

  onInput(event) {
    const field = event.target
    const maxLength = Number(field.dataset.maxLength)
    field.value = field.value.replace(/\D/g, "").slice(0, maxLength)

    if (field.value.length === maxLength) {
      const segments = this.segments()
      const next = segments[segments.indexOf(field) + 1]
      if (next) next.focus()
    }

    this.sync()
  }

  // Backspacing on an already-empty box jumps back to the previous one,
  // matching how one-time-code inputs behave — feels natural to type through.
  onKeydown(event) {
    if (event.key !== "Backspace" || event.target.value.length > 0) return

    const segments = this.segments()
    const previous = segments[segments.indexOf(event.target) - 1]
    if (previous) previous.focus()
  }

  // Pads a lone digit ("9" -> "09") once the person tabs/clicks away, so a
  // quickly-typed single digit still lands on the right hour/day.
  onBlur(event) {
    const field = event.target
    const maxLength = Number(field.dataset.maxLength)
    if (field.value.length > 0 && field.value.length < maxLength) {
      field.value = field.value.padStart(maxLength, "0")
    }
    this.sync()
  }

  sync() {
    const [day, month, year, hour, minute] = this.segments().map((field) => field.value)

    if (day.length === 2 && month.length === 2 && year.length === 4) {
      this.hiddenTarget.value = `${year}-${month}-${day}T${(hour || "00").padStart(2, "0")}:${(minute || "00").padStart(2, "0")}`
    } else {
      this.hiddenTarget.value = ""
    }
  }
}
